'use strict'

import Dropcolor from '../Dropcolor'
import Color from '../models/Color'

/**
 * ColorInfoModal
 * Modal controller for viewing/editing color details (surface name, note, stats).
 * Keeps UI in sync with the in-memory Color object and inline dropcolor info.
 */
class ColorInfoModal {
    /**
     * @param {Document} root - document reference for querying modal elements.
     */
    constructor(root = document) {
        this.root = root
        this.currentSlot = null
        this.application = null

        this.els = {
            modal: root.getElementById('color-info-modal'),
            backdrop: root.getElementById('color-info-backdrop'),
            close: root.getElementById('color-info-close'),
            title: root.querySelector('[data-color-title]'),
            code: root.querySelector('[data-color-code]'),
            colorSample: root.querySelector('[data-color-sample]'),
            page: root.querySelector('[data-color-page]'),
            lrv: root.querySelector('[data-color-lrv]'),
            labL: root.querySelector('[data-color-lab-l]'),
            labA: root.querySelector('[data-color-lab-a]'),
            labB: root.querySelector('[data-color-lab-b]'),
            srgb: root.querySelector('[data-color-srgb]'),
            swatch: root.querySelector('[data-color-swatch]'),
            nameInput: root.querySelector('[data-color-name-input]'),
            noteInput: root.querySelector('[data-color-note-input]'),
            colorList: root.querySelector('[data-color-list]'),
            deleteBtn: root.getElementById('color-info-delete'),
            removeTextureBtn: root.querySelector('[data-color-remove-texture]'),
        }

        this._bindUI()
    }

    /**
     * Open the modal and render current color data.
     * @param {Object} params
     * @param {number} params.slot - dropcolor slot index.
     * @param {Object} params.color - Color model instance.
     * @param {Map<number, Object>} params.colors - map of slot → Color.
     * @param {Object} params.application - Application instance for canvasState access.
     * @returns {void}
     */
    open({ slot, color, colors, application } = {}) {
        if (!this.els.modal || !color) return

        this.currentSlot = slot
        this.application = application

        this._renderHeader(color)
        this._renderMeta(color)
        this._renderStats(color)
        this._renderSwatch(color)
        this._renderDetails(color)
        this._renderColorList(colors)

        this.els.modal.classList.add('is-open')
    }

    /**
     * Wire UI events: close, delete, and inline edits.
     * @returns {void}
     */
    _bindUI() {
        if (this.els.close) {
            this.els.close.addEventListener('click', () => this.close())
        }
        if (this.els.backdrop) {
            this.els.backdrop.addEventListener('click', () => this.close())
        }
        if (this.els.deleteBtn) {
            this.els.deleteBtn.addEventListener('click', () => this._handleDelete())
        }
        if (this.els.removeTextureBtn) {
            this.els.removeTextureBtn.addEventListener('click', () => {
                this._handleRemoveTexture().catch((error) => {
                    console.error('[ColorInfoModal] remove texture failed:', error)
                })
            })
        }
        if (this.els.nameInput) {
            this.els.nameInput.addEventListener('input', (e) => this._updateName(e.target.value))
            this.els.nameInput.addEventListener('blur', (e) => this._updateName(e.target.value, true))
        }
        if (this.els.noteInput) {
            this.els.noteInput.addEventListener('input', (e) => this._updateNote(e.target.value))
            this.els.noteInput.addEventListener('blur', (e) => this._updateNote(e.target.value))
        }
    }

    /**
     * Close the modal and refresh inline info to reflect edits.
     * @returns {void}
     */
    close() {
        if (!this.els.modal) return
        this.els.modal.classList.remove('is-open')
        this._refreshInlineInfo()
    }

    /**
     * Render top title/code.
     * @param {Object} color - Color model instance.
     * @returns {void}
     */
    _renderHeader(color) {
        const swatchColor = color.swatch_color || {}

        if (this.els.title) {
            this.els.title.textContent = swatchColor.name2 || color.name || swatchColor.name || ''
        }
        if (this.els.code) {
            this.els.code.textContent = swatchColor.name || swatchColor.name2 || ''
        }
    }

    /**
     * Render color sample + page metadata.
     * @param {Object} color - Color model instance.
     * @returns {void}
     */
    _renderMeta(color) {
        const swatch = color.swatch || {}
        const page = color.swatch_page || {}

        if (this.els.colorSample) {
            this.els.colorSample.textContent = swatch.name || swatch.swatchName || ''
        }
        if (this.els.page) {
            const pageLabel = page.page_number ?? page.page_name
            this.els.page.textContent = pageLabel != null ? pageLabel : ''
        }
    }

    /**
     * Render numeric stats (LRV, Lab, sRGB).
     * @param {Object} color - Color model instance.
     * @returns {void}
     */
    _renderStats(color) {
        const swatchColor = color.swatch_color || {}

        const fmt = (value, decimals = 2) => {
            const n = Number(value)
            return Number.isFinite(n) ? n.toFixed(decimals) : '-'
        }
        const fmtInt = (value) => {
            const n = Number(value)
            return Number.isFinite(n) ? Math.round(n) : '-'
        }

        if (this.els.lrv) this.els.lrv.textContent = fmtInt(swatchColor.reflectance)
        if (this.els.labL) this.els.labL.textContent = fmt(swatchColor.labl)
        if (this.els.labA) this.els.labA.textContent = fmt(swatchColor.laba)
        if (this.els.labB) this.els.labB.textContent = fmt(swatchColor.labb)

        if (this.els.srgb) {
            const { sr, sg, sb } = swatchColor
            const valid = [sr, sg, sb].every((v) => Number.isFinite(Number(v)))
            this.els.srgb.textContent = valid ? `${Math.round(sr)} ${Math.round(sg)} ${Math.round(sb)}` : '-'
        }
    }

    /**
     * Render swatch preview (color and optional texture image).
     * @param {Object} color - Color model instance.
     * @returns {void}
     */
    _renderSwatch(color) {
        if (!this.els.swatch) return
        const swatchColor = color.swatch_color || {}
        const canRemoveTexture = color.type === Color.TYPE.TEXTURE_SWATCH && !!color.texture_swatch?.id

        this.els.swatch.style.backgroundColor = ''
        this.els.swatch.style.backgroundImage = ''

        if (this.els.removeTextureBtn) {
            this.els.removeTextureBtn.hidden = !canRemoveTexture
        }

        if (color.texture_full_url) {
            this.els.swatch.style.backgroundImage = `url(${color.texture_full_url})`
        }

        if (Number.isFinite(Number(swatchColor.sr))) {
            const { sr, sg, sb } = swatchColor
            this.els.swatch.style.backgroundColor = `rgb(${sr}, ${sg}, ${sb})`
        }
    }

    /**
     * Render editable fields (name, note).
     * @param {Object} color - Color model instance.
     * @returns {void}
     */
    _renderDetails(color) {
        const swatchColor = color.swatch_color || {}
        color.applyDefaultNameIfEmpty()
        if (this.els.nameInput) {
            this.els.nameInput.value = color.name || swatchColor.name2 || swatchColor.name || ''
        }
        if (this.els.noteInput) {
            this.els.noteInput.value = color.note || ''
        }
    }

    /**
     * Render footer chips to switch between colors.
     * @param {Map<number, Object>} colorsMap - map of slot → Color.
     * @returns {void}
     */
    _renderColorList(colorsMap) {
        if (!this.els.colorList) return
        this.els.colorList.innerHTML = ''
        if (!colorsMap) return

        colorsMap.forEach((color, slot) => {
            const swatchColor = color?.swatch_color || {}
            const chip = this.root.createElement('button')
            chip.type = 'button'
            chip.className = 'color-info-chip'
            chip.dataset.slot = slot
            chip.title = swatchColor.name || `Color ${slot}`

            if (Number.isFinite(Number(swatchColor.sr))) {
                const { sr, sg, sb } = swatchColor
                chip.style.backgroundColor = `rgb(${sr}, ${sg}, ${sb})`
            }
            if (slot === this.currentSlot) {
                chip.classList.add('is-active')
            }

            chip.addEventListener('click', () => this._switchToSlot(slot))
            this.els.colorList.appendChild(chip)
        })
    }

    /**
     * Switch active slot from footer chips and reopen details.
     * @param {number} slot - dropcolor slot index.
     * @returns {void}
     */
    _switchToSlot(slot) {
        if (!slot) return
        const slotId = Dropcolor.slotId(slot)
        const el = this.root.getElementById(slotId)
        if (el) {
            el.click()
            document.dispatchEvent(
                new CustomEvent('viz:dropcolorDetailsRequested', {
                    detail: { slot },
                }),
            )
        }
    }

    /**
     * Trigger delete via existing dropcolor delete button.
     * @returns {void}
     */
    _handleDelete() {
        const pos = this.currentSlot || this.application?.canvasState?.colorSample?.workingColorPos
        if (!pos) return

        const deleteBtn = this.root.getElementById(`deletedropcolor${pos}`)
        if (deleteBtn) {
            deleteBtn.click()
            this.close()
        }
    }

    /**
     * Remove only the applied texture swatch from the current color.
     * Keeps the underlying swatch color, refreshes the preview and repaints the canvas.
     * @returns {Promise<void>}
     */
    async _handleRemoveTexture() {
        const color = this._getColor()
        const app = this.application
        const canvasState = app?.canvasState
        const colorSample = canvasState?.colorSample
        if (!color || !canvasState || !colorSample) return
        if (color.type !== Color.TYPE.TEXTURE_SWATCH || !color.texture_swatch?.id) return

        colorSample.workingColorPos = this.currentSlot
        color.clearTextureAssignment()
        colorSample.setCurrentColor(color)
        colorSample.updateColorInfos()
        await canvasState.rebuildPaintedImage()
        await app?.paintingListener?.dropHandler?._highlightSourceSwatch?.(color)

        this._renderSwatch(color)
        this._refreshInlineInfo()
    }

    /**
     * Helper to get current Color from CanvasState.
     * @param {number} [slot=this.currentSlot] - dropcolor slot index.
     * @returns {Object|undefined}
     */
    _getColor(slot = this.currentSlot) {
        return this.application?.canvasState?.colorSample?.getColorByNumber(slot)
    }

    /**
     * Persist name edits to the Color model and refresh UI.
     * @param {string} value - user-entered name value.
     * @param {boolean} restoreDefault - restore default name on empty blur.
     * @returns {void}
     */
    _updateName(value, restoreDefault = false) {
        const color = this._getColor()
        if (!color) return

        color.name = value || ''

        if (restoreDefault && !color.name.trim()) {
            color.name = ''
            color.applyDefaultNameIfEmpty(this.currentSlot)
            this.els.nameInput.value = color.name
        }

        this._renderHeader(color)
        this._refreshInlineInfo()
    }

    /**
     * Persist note edits to the Color model and refresh UI.
     * @param {string} value - user-entered note value.
     * @returns {void}
     */
    _updateNote(value) {
        const color = this._getColor()
        if (!color) return
        color.note = value || ''
        this._refreshInlineInfo()
    }

    /**
     * Sync inline dropcolor info with current Color data.
     * @returns {void}
     */
    _refreshInlineInfo() {
        if (!this.currentSlot) return
        const color = this._getColor(this.currentSlot)
        if (color) {
            // Update the dataset and inline text directly from the current color
            Dropcolor.updateInfoPanel(color, this.root)
            return
        }

        const slotEl = this.root.getElementById(Dropcolor.slotId(this.currentSlot))
        if (slotEl) {
            Dropcolor.updateInfoPanelFromSlot(slotEl, this.root)
        }
    }
}

export default ColorInfoModal
